import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PublicNav from "@/components/PublicNav";
import { ArrowLeft, Save, Plus, X } from "lucide-react";

const WEIGHT_LABELS = {
  offres_volume: "Pertinence et volume des offres d'emploi",
  adequation_candidats: "Adéquation métiers ↔ bassin de candidats NEXA",
  signaux_international: "Signaux documentés de recrutement international",
  frequence_besoins: "Fréquence des besoins de recrutement",
  actualite_fiabilite: "Actualité et fiabilité des informations",
  coordonnees: "Qualité des coordonnées professionnelles",
};

const THRESHOLD_LABELS = {
  haute: "Priorité élevée (≥)",
  intermediaire: "Priorité intermédiaire (≥)",
  a_surveiller: "Potentiel à surveiller (≥)",
};

const RULE_LABELS = {
  max_emails_per_day: "Courriels maximum par jour",
  cooldown_days: "Délai minimum entre deux messages (jours)",
  max_relances: "Nombre maximum de relances",
};

export default function ProspectionConfig() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [newSector, setNewSector] = useState("");
  const [weights, setWeights] = useState({});
  const [thresholds, setThresholds] = useState({});
  const [rules, setRules] = useState({});
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const allowed = user && user.role === "super_admin";

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
    else if (!authLoading && user && user.role !== "super_admin") navigate("/prospection", { replace: true });
  }, [user, authLoading, navigate]);

  const load = useCallback(async () => {
    const res = await api.get("/prospection/config");
    setConfig(res.data);
    setSectors(res.data.priority_sectors || []);
    setWeights(res.data.score_weights || {});
    setThresholds(res.data.band_thresholds || {});
    setRules(res.data.outreach_rules || {});
  }, []);

  useEffect(() => {
    if (allowed) load().catch((e) => setError(formatApiError(e)));
  }, [allowed, load]);

  if (!user || !allowed) return null;

  const weightsTotal = Object.values(weights).reduce((a, b) => a + (Number(b) || 0), 0);

  const save = async (patch) => {
    setError(""); setSaved(false);
    try {
      await api.patch("/prospection/config", patch);
      setSaved(true);
      await load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const inputCls = "text-sm border border-slate-200 rounded-lg px-3 py-2 w-24 text-center";

  return (
    <div data-testid="prospection-config-page" className="min-h-screen bg-slate-50">
      <PublicNav />
      <div className="bg-brand-navy">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <Link data-testid="config-back" to="/prospection"
            className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-xs font-semibold mb-3">
            <ArrowLeft className="w-3.5 h-3.5" /> Prospection
          </Link>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white">Configuration du module de prospection</h1>
          <p className="text-white/70 text-sm mt-1">Secteurs prioritaires, pondérations du score, seuils et règles d'envoi — modifiables sans déploiement.</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {error && <div data-testid="config-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}
        {saved && <div data-testid="config-saved" className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">Configuration enregistrée et journalisée.</div>}
        {!config && <p className="text-sm text-slate-400">Chargement…</p>}

        {config && (
          <>
            <section data-testid="config-sectors" className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
              <h2 className="font-bold text-brand-navy">Secteurs prioritaires</h2>
              <div className="flex flex-wrap gap-2">
                {sectors.map((s) => (
                  <span key={s} data-testid={`sector-chip-${s}`} className="flex items-center gap-1.5 text-xs font-medium bg-brand-sky text-brand-navy rounded-full px-3 py-1.5">
                    {s.replaceAll("_", " ")}
                    <button data-testid={`sector-remove-${s}`} onClick={() => setSectors(sectors.filter((x) => x !== s))}
                      className="text-slate-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                <input data-testid="sector-new-input" value={newSector} onChange={(e) => setNewSector(e.target.value)}
                  placeholder="Nouveau secteur…" className="text-sm border border-slate-200 rounded-lg px-3 py-2 flex-1 min-w-[180px] max-w-xs" />
                <button data-testid="sector-add-btn" onClick={() => { if (newSector.trim()) { setSectors([...sectors, newSector.trim()]); setNewSector(""); } }}
                  className="flex items-center gap-1 text-xs font-semibold text-brand-navy border border-slate-200 rounded-lg px-3 py-2 hover:border-brand-turquoise">
                  <Plus className="w-3.5 h-3.5" /> Ajouter
                </button>
                <button data-testid="sectors-save-btn" onClick={() => save({ priority_sectors: sectors })}
                  className="flex items-center gap-1.5 text-xs font-semibold bg-brand-turquoise text-white rounded-lg px-4 py-2 hover:bg-brand-turquoise/90 ml-auto">
                  <Save className="w-3.5 h-3.5" /> Enregistrer les secteurs
                </button>
              </div>
            </section>

            <section data-testid="config-weights" className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
              <h2 className="font-bold text-brand-navy">Pondérations du score de priorité (total = 100)</h2>
              {Object.keys(WEIGHT_LABELS).map((k) => (
                <div key={k} className="flex items-center gap-3 text-sm">
                  <span className="flex-1 text-slate-600">{WEIGHT_LABELS[k]}</span>
                  <input data-testid={`weight-${k}`} type="number" min="0" max="100" value={weights[k] ?? 0}
                    onChange={(e) => setWeights({ ...weights, [k]: Number(e.target.value) })} className={inputCls} />
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <span data-testid="weights-total" className={`text-sm font-semibold ${weightsTotal === 100 ? "text-green-700" : "text-red-600"}`}>
                  Total : {weightsTotal} / 100
                </span>
                <button data-testid="weights-save-btn" onClick={() => save({ score_weights: weights })} disabled={weightsTotal !== 100}
                  className="flex items-center gap-1.5 text-xs font-semibold bg-brand-turquoise text-white rounded-lg px-4 py-2 hover:bg-brand-turquoise/90 disabled:opacity-40">
                  <Save className="w-3.5 h-3.5" /> Enregistrer les pondérations
                </button>
              </div>
            </section>

            <section data-testid="config-thresholds" className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
              <h2 className="font-bold text-brand-navy">Seuils de priorité</h2>
              {Object.keys(THRESHOLD_LABELS).map((k) => (
                <div key={k} className="flex items-center gap-3 text-sm">
                  <span className="flex-1 text-slate-600">{THRESHOLD_LABELS[k]}</span>
                  <input data-testid={`threshold-${k}`} type="number" min="0" max="100" value={thresholds[k] ?? 0}
                    onChange={(e) => setThresholds({ ...thresholds, [k]: Number(e.target.value) })} className={inputCls} />
                </div>
              ))}
              <div className="flex justify-end pt-2 border-t border-slate-100">
                <button data-testid="thresholds-save-btn" onClick={() => save({ band_thresholds: thresholds })}
                  className="flex items-center gap-1.5 text-xs font-semibold bg-brand-turquoise text-white rounded-lg px-4 py-2 hover:bg-brand-turquoise/90">
                  <Save className="w-3.5 h-3.5" /> Enregistrer les seuils
                </button>
              </div>
            </section>

            <section data-testid="config-rules" className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
              <h2 className="font-bold text-brand-navy">Règles de prospection commerciale</h2>
              {Object.keys(RULE_LABELS).map((k) => (
                <div key={k} className="flex items-center gap-3 text-sm">
                  <span className="flex-1 text-slate-600">{RULE_LABELS[k]}</span>
                  <input data-testid={`rule-${k}`} type="number" min="0" value={rules[k] ?? 0}
                    onChange={(e) => setRules({ ...rules, [k]: Number(e.target.value) })} className={inputCls} />
                </div>
              ))}
              <div className="flex justify-end pt-2 border-t border-slate-100">
                <button data-testid="rules-save-btn" onClick={() => save({ outreach_rules: rules })}
                  className="flex items-center gap-1.5 text-xs font-semibold bg-brand-turquoise text-white rounded-lg px-4 py-2 hover:bg-brand-turquoise/90">
                  <Save className="w-3.5 h-3.5" /> Enregistrer les règles
                </button>
              </div>
              <p className="text-xs text-slate-400">Ces règles s'appliquent aux campagnes et relances ; la conformité LCAP reste vérifiée avant chaque envoi, quelle que soit la configuration.</p>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
